import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { SupabaseQueryChain } from '@/types/supabase-helpers'

/* ============================================================
   CRUD Hook Factory
   Generates typed useList / useOne / useCreate / useUpdate / useDelete
   hooks for any Supabase table with automatic cache invalidation.
   PRD §10 — create-crud-hooks pattern
   ============================================================ */

type TableName = keyof import('@/types/database.types').Database['public']['Tables']

interface CrudFactoryOptions<T> {
  table: TableName
  queryKey: string
  orderBy?: keyof T & string
  orderAsc?: boolean
  /** Filter callback applied to the Supabase query builder */
  defaultFilter?: (query: SupabaseQueryChain) => SupabaseQueryChain
}

/**
 * Build a reusable CRUD hook bundle for the given Supabase table.
 *
 * Each hook is tenant-scoped via RLS and invalidates the shared
 * `queryKey` so downstream components always see fresh data.
 *
 * @param options - Table, queryKey, ordering, and optional default filters
 * @returns Object containing useList, useOne, useCreate, useUpdate, useDelete hooks
 */
export function createCrudHooks<Row, Insert = Partial<Row>, Update = Partial<Row>>(
  options: CrudFactoryOptions<Row>
) {
  const { table, queryKey, orderBy, orderAsc = true, defaultFilter } = options

  /**
   * List all rows (tenant-scoped via RLS).
   *
   * @param tenantId - Tenant slug or ID to scope the query (disabled when missing)
   * @returns TanStack Query result for the row array
   */
  function useList(tenantId?: string) {
    return useQuery<Row[]>({
      queryKey: [queryKey, 'list', tenantId],
      queryFn: async () => {
        let query = supabase.from(table).select('*') as unknown as SupabaseQueryChain
        if (tenantId) query = query.eq('tenant_id', tenantId)
        if (defaultFilter) query = defaultFilter(query)
        if (orderBy) query = query.order(orderBy, { ascending: orderAsc })

        const { data, error } = await query
        if (error) throw error
        return (data ?? []) as Row[]
      },
      enabled: !!tenantId,
    })
  }

  /**
   * Fetch a single row by ID (tenant-scoped).
   *
   * @param id - Row ID
   * @param tenantId - Tenant ID for RLS
   * @returns TanStack Query result containing the row or null
   */
  function useOne(id: string | undefined, tenantId?: string) {
    return useQuery<Row | null>({
      queryKey: [queryKey, 'one', id, tenantId],
      queryFn: async () => {
        const { data, error } = await (
          supabase.from(table).select('*') as unknown as SupabaseQueryChain
        )
          .eq('id', id!)
          .eq('tenant_id', tenantId!)
          .single()
        if (error) throw error
        return data as Row
      },
      enabled: !!id && !!tenantId,
    })
  }

  /**
   * Create a new row mutation.
   *
   * @returns Mutation hook returning the inserted row
   */
  function useCreate() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (values: Insert) => {
        const { data, error } = await (supabase.from(table) as unknown as SupabaseQueryChain)
          .insert(values as Record<string, unknown>)
          .select()
          .single()
        if (error) throw error
        return data as Row
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [queryKey] })
      },
    })
  }

  /**
   * Update an existing row.
   *
   * @returns Mutation hook with id/value payload
   */
  function useUpdate() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async ({ id, ...values }: Update & { id: string }) => {
        const tbl = supabase.from(table) as unknown as SupabaseQueryChain
        const { data, error } = await tbl.update(values).eq('id', id).select().single()
        if (error) throw error
        return data as Row
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [queryKey] })
      },
    })
  }

  /**
   * Delete a row by ID.
   *
   * @returns Mutation hook for removing the given row
   */
  function useDelete() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (id: string) => {
        const { error } = await (supabase.from(table).delete() as unknown as SupabaseQueryChain).eq(
          'id',
          id
        )
        if (error) throw error
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [queryKey] })
      },
    })
  }

  return { useList, useOne, useCreate, useUpdate, useDelete }
}
